/**
 * gamMediation.js — is this GAM response a mediation / SDK-bidding fill?
 *
 * When an SDK-bidding or mediation network wins, GAM's response carries NO
 * creative: `ad_type` is "" and there is no ad_html. Instead `ad_networks`
 * names the adapter the Google SDK must hand rendering to (iOS
 * "GADMediationAdapterVungle", Android
 * "com.google.ads.mediation.vungle.VungleMediationAdapter") and the response
 * header says x-afma-mediation: true. The old rule ("empty ad_type + no
 * ad_html = no fill") read every such win as a blank slot and suppressed
 * impression matching — measured on a real Liftoff win (2026-10-08).
 *
 * Twin of proxy-server/gamMediation.js — keep the logic identical;
 * test/gamMediationParity.test.js fails if they drift. Pure, never throws.
 */

function adapterNames(net) {
  const out = [];
  for (const k of ['adapter_class_names', 'adapters']) {
    const v = net && net[k];
    if (Array.isArray(v)) {
      for (const x of v) if (typeof x === 'string' && x.trim()) out.push(x.trim());
    } else if (typeof v === 'string' && v.trim()) {
      out.push(v.trim());
    }
  }
  return out;
}

/** "GADMediationAdapterVungle" / "com.google.ads.mediation.vungle.VungleMediationAdapter" -> "Vungle" */
function networkFromAdapter(name) {
  const s = String(name || '');
  let m = /^GADMediationAdapter([A-Za-z0-9]+)$/.exec(s);
  if (m) return m[1];
  m = /\.([A-Za-z0-9]+)MediationAdapter$/.exec(s) || /([A-Za-z0-9]+)MediationAdapter$/.exec(s) || /([A-Za-z0-9]+)Adapter$/.exec(s);
  return m ? m[1] : s;
}

/**
 * @param {object} parsed GAM JSON response (already parsed)
 * @returns {{ adapter: string, network: string } | null}
 */
function mediationFillOf(parsed) {
  try {
    const nets = parsed && Array.isArray(parsed.ad_networks) ? parsed.ad_networks : [];
    for (const net of nets) {
      const names = adapterNames(net);
      if (names.length) return { adapter: names[0], network: networkFromAdapter(names[0]) };
    }
  } catch (_) { /* never throw */ }
  return null;
}

/**
 * The mediation network's own impression pings, from `ad_networks[0].imp_urls`:
 * Google's mediation impression ping (pagead/adview?ai=…) and the network's
 * billing notice (e.g. Liftoff events.ads.vungle.com/api/bill?settlement_price=…).
 * Stored compactly as validData.expanded.mediationImpUrls so the lifecycle can
 * join the pings the SDK later fires back to this slot after the body is gone.
 * @param {object} parsed GAM JSON response (already parsed)
 * @returns {string[]} up to 10 URLs (empty when none)
 */
function mediationImpUrlsOf(parsed) {
  try {
    const nets = parsed && Array.isArray(parsed.ad_networks) ? parsed.ad_networks : [];
    const urls = nets[0] && Array.isArray(nets[0].imp_urls) ? nets[0].imp_urls : [];
    return urls.filter((u) => typeof u === 'string' && u.length > 10).slice(0, 10);
  } catch (_) { /* never throw */ }
  return [];
}

export { mediationFillOf, networkFromAdapter, mediationImpUrlsOf };
